import { z } from "zod";

// Shared
export const PropertyKindEnum = z.enum(["ENUM", "COLOR"]);

// Property Option (input)
export const PropertyOptionInputSchema = z.object({
  value_code: z.string().min(1),
  value_en: z.string().min(1),
  value_fa: z.string().optional(),
  hex: z.string().regex(/^#([0-9A-Fa-f]{3}){1,2}$/).optional(),
});
export type PropertyOptionInput = z.infer<typeof PropertyOptionInputSchema>;

// Property Option (patch)
export const PropertyOptionPatchSchema = z.object({
  value_code: z.string().min(1).optional(),
  value_en: z.string().min(1).optional(),
  value_fa: z.string().optional(),
  hex: z
    .string()
    .regex(/^#([0-9A-Fa-f]{3}){1,2}$/)
    .optional(),
});
export type PropertyOptionPatch = z.infer<typeof PropertyOptionPatchSchema>;

// Property (input)
export const PropertyInputSchema = z.object({
  code: z.string().min(1),
  label_en: z.string().min(1),
  label_fa: z.string().optional(),
  kind: PropertyKindEnum,
  groupId: z.string().cuid(),
  options: z
    .array(PropertyOptionInputSchema)
    .min(1, { message: "At least one option is required." }),
});
export type PropertyInput = z.infer<typeof PropertyInputSchema>;

// Bulk create/update payloads
export const PropertiesBulkCreateSchema = z.object({
  properties: z.array(PropertyInputSchema).min(1),
});
export type PropertiesBulkCreate = z.infer<typeof PropertiesBulkCreateSchema>;

// Property Group (input)
export const PropertyGroupInputSchema = z.object({
  name_en: z.string().min(1),
  slug: z
    .string()
    .min(1)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
  sortOrder: z.number().int().nonnegative().optional(),
});
export type PropertyGroupInput = z.infer<typeof PropertyGroupInputSchema>;

// Responses
export const PropertyGroupSchema = PropertyGroupInputSchema.extend({
  id: z.string().cuid(),
});
export type PropertyGroup = z.infer<typeof PropertyGroupSchema>;

// Patch schemas
export const PropertyPatchSchema = z.object({
  code: z.string().min(1).optional(),
  label_en: z.string().min(1).optional(),
  label_fa: z.string().optional(),
  kind: PropertyKindEnum.optional(),
  groupId: z.string().cuid().optional(),
  optionsOrder: z.array(z.string().cuid()).optional(),
  newOptions: z.array(PropertyOptionInputSchema).optional(),
});
export type PropertyPatch = z.infer<typeof PropertyPatchSchema>;

export const PropertyGroupPatchSchema = z.object({
  name_en: z.string().min(1).optional(),
  slug: z
    .string()
    .min(1)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)
    .optional(),
  sortOrder: z.number().int().nonnegative().optional(),
});
export type PropertyGroupPatch = z.infer<typeof PropertyGroupPatchSchema>;

// UI-facing helper types (no separate types file)
export type PropertyKindT = z.infer<typeof PropertyKindEnum>;
export type OptionRowUI = {
  id: string;
  value_code: string;
  value_en: string;
  value_fa: string;
  hex?: string;
};
export type PropertyUI = {
  id: string;
  code: string;
  label_en: string;
  label_fa: string;
  kind: PropertyKindT;
  options: OptionRowUI[];
  groupId: string | null;
  sortOrder?: number | null;
};
